import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmbedForm } from './components/EmbedForm';
import { EmbedViewer } from './components/EmbedViewer';

import { useWidgetStore } from '@/common/stores/useWidgetStore';

interface EmbedProps {
  widgetId: string;
}

export default function Embed({ widgetId }: EmbedProps) {
  const { t } = useTranslation();
  const { widgets, updateWidget } = useWidgetStore();
  const widget = widgets.find(w => w.id === widgetId);
  const initialUrl = widget?.metadata?.url as string;
  const [url, setUrl] = useState(initialUrl || '');
  const [isEditing, setIsEditing] = useState(!initialUrl);

  const handleSave = async () => {
    if (!widget) return;
    await updateWidget(widgetId, { metadata: { url } });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setUrl(initialUrl || '');
    setIsEditing(false);
  };

  useEffect(() => {
    const currentUrl = widget?.metadata?.url as string;
    if (currentUrl) setUrl(currentUrl);
  }, [widget?.metadata?.url]);

  if (isEditing) {
    return (
      <EmbedForm
        initialUrl={initialUrl}
        url={url}
        onCancel={handleCancel}
        onSave={handleSave}
        onUrlChange={(e) => setUrl(e.target.value)}
      />
    );
  }

  if (!url) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <button
          className="px-6 py-3 text-blue-500 hover:text-blue-600 bg-white rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2"
          onClick={() => setIsEditing(true)}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
          {t('embed.addUrl')}
        </button>
      </div>
    );
  }

  return <EmbedViewer url={url} onEdit={() => setIsEditing(true)} />;
}
